<template>
	<span v-if="visible" class="fd-datetime" :class="{ 'fd-datetime-optional': optional }"
	><span class="fd-datetime-formatted">{{ formatted }}</span
	><a-switch v-if="optional && !ro" v-model="notEmpty" class="fd-datetime-switch"></a-switch
	></span>
</template>

<script setup lang="ts">
import ASwitch from "../../components/ASwitch.vue";
import useEditor, {type EditorProps, editorProps} from "../Editor";
import {computed} from "vue";
import {injectEnv} from "../../Environment";
import type {DateTime} from "@t/fd/editor/DateTime";
import {formatDate, parseDate} from "@t/util/DateUtil";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;

const dt = computed<DateTime>(() => props.part as DateTime);
const env = injectEnv();

const {visible, value, clazz, style, ro, optional} = useEditor(props);

const policy = computed(() => {
	switch (dt.value.timePolicy) {
		case 0:
			return "date";
		case 1:
			return "universal";
		default:
			return "datetime";
	}
});
const notEmpty = computed({
	get() {
		return parsedDate.value.date != null;
	},
	set(v) {
		if (v) {
			if (!value.value) {
				let dd = new Date();
				if (policy.value !== "date") {
					dd.setHours(10);
					dd.setMinutes(0);
					dd.setSeconds(0);
					if (dd.getTime() < new Date().getTime()) dd.setDate(dd.getDate() + 1);
				}
				value.value = formatDate(dd, policy.value !== "date");
			}
		} else {
			value.value = null;
		}
	},
});
const parsedDate = computed(() => {
	return parseDate(value.value);
});
const emptyDate = computed(() => {
	if (dt.value.empty) return dt.value.empty;
	return "";
});
const formatted = computed(() => {
	if (!value.value) return emptyDate.value;
	switch (dt.value.format) {
		case "date":
			return formattedDate.value;
		case "time":
			return formattedTime.value;
		default:
			return formattedStandard.value;
	}
});
const formattedDate = computed(() => {
	const parsed = parsedDate.value;
	return parsed.date ? env.formats.dateFormat.format(parsed.date) : emptyDate.value;
});
const formattedTime = computed(() => {
	const parsed = parsedDate.value;
	return parsed.date && !parsed.wholeDay ? env.formats.hourMinFormat.format(parsed.date) : emptyDate.value;
});
const formattedStandard = computed(() => {
	const parsed = parsedDate.value;
	return parsed.date ? (parsed.wholeDay ? env.formats.dateFormat.format(parsed.date) : env.formats.dateTimeFormat.format(parsed.date)) : emptyDate.value;
});
</script>

<style>
.fd-datetime-optional {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	column-gap: 8px;
	line-height: 30px;
	min-height: 30px;
}
</style>
